import React, { FC } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ClaimAdditionalAccountModal } from '@blocs.auth';
import { useTranslation, Trans } from '@blocs.i18n';
import { useModal } from '@minecraft.modal';
import SCREEN_NAMES from 'config/constants/screenNames';
import routeHelper from 'utils/routeHelper';
import { MenuButton, MenuItem, RightIcon, SubMenu, SubMenuItems } from 'components/core/NavLoggedIn/Styles';
import { gqlSubscriptionStatusEnum } from '@minecraft.graphql-types';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import { useArtistContext } from '../ArtistContext';

interface CurrentArtistSubMenuProps {
  accountSettingsLink: string;
  notificationLink: { to: string };
}

const CurrentArtistSubMenu: FC<CurrentArtistSubMenuProps> = ({ accountSettingsLink, notificationLink }) => {
  const { t } = useTranslation();
  const { subscriptionStatus } = useArtistContext();
  const { isOpen, open, close } = useModal();
  const isSubscriptionPaused = subscriptionStatus === gqlSubscriptionStatusEnum.PAUSED;
  const isLegacyUserOnboardingDeprecated = useFeatureExperiment(FEATURE_FLAGS.WEB_ONBOARDING_DEPRECATION);

  return (
    <>
      <SubMenu>
        <MenuButton data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-account-settings`}>
          <Trans
            i18nKey="common:accountSettingsMenu.accountSettings"
            components={[<RightIcon name="caret-right" color="black" />]}
          />
        </MenuButton>
        <SubMenuItems>
          <MenuItem component="span">
            <Link data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-account-details`} to={accountSettingsLink}>
              {t('common:title.accountDetails')}
            </Link>
          </MenuItem>
          {!isSubscriptionPaused && (
            <MenuItem component="span">
              <NavLink
                {...notificationLink}
                data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-notification-settings`}
                role="button"
              >
                {t('common:accountSettingsMenu.notificationSettings')}
              </NavLink>
            </MenuItem>
          )}
          <MenuItem component="span">
            <NavLink
              {...routeHelper.membershipOverview.forLink()}
              data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-membership-billing`}
            >
              {t('common:accountSettingsMenu.membershipAndBilling')}
            </NavLink>
          </MenuItem>
          {!isSubscriptionPaused && (
            <MenuItem component="span">
              <NavLink
                {...routeHelper.profileSettingsTalents.forLink()}
                data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-profile-settings`}
              >
                {t('common:accountSettingsMenu.profileSettings')}
              </NavLink>
            </MenuItem>
          )}
        </SubMenuItems>
      </SubMenu>
      {!isSubscriptionPaused && (
        <MenuItem component="span">
          <NavLink
            {...routeHelper.talentDisambiguation.forLink()}
            data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-manage-talent`}
          >
            {t('common:accountSettingsMenu.manageTalent')}
          </NavLink>
        </MenuItem>
      )}
      {!isSubscriptionPaused && !isLegacyUserOnboardingDeprecated && (
        <MenuButton onClick={open}>{t('common:accountSettingsMenu.claimAdditionalAccounts')}</MenuButton>
      )}
      <ClaimAdditionalAccountModal isOpen={isOpen} close={close} />
    </>
  );
};

export default CurrentArtistSubMenu;
